import { useState, useEffect } from "react";
import FEPaginatedTable from "@components/Tables/FEPaginatedTable/FEPaginatedTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { getSurveyTags } from "../SurveyHelpers/surveyHelpers";
import { sortItemsByAttribute, sortItemsByDate } from "../api";
import { capitalizeFirstLetter, splitPascalCase } from "@utils/helperFunctions";

const ManageSurveys = ({
  surveysData,
  filtering,
  selected,
  setSelected,
}): JSX.Element => {
  const [sortedQuestionnaires, setSortedQuestionnaires] = useState(surveysData);
  const [sortBy, setSortBy] = useState({
    name: "",
    status: "",
    createdOn: "",
    tags: "",
  });

  useEffect(() => {
    if (surveysData.data) {
      setSortedQuestionnaires({
        ...sortedQuestionnaires,
        data: surveysData.data,
      });
      for (const [key, value] of Object.entries(sortBy)) {
        if (value !== "") {
          if (key == "name") {
            handleSort("Survey Name", true);
          } else {
            handleSort(splitPascalCase(capitalizeFirstLetter(key)), true);
          }
        }
      }
    }
  }, [surveysData, selected]);

  const columnHeaders = [
    {
      label: "Survey Name",
      width: 3,
      sorted: sortBy.name,
      sortable: true,
    },
    {
      label: "Status",
      width: 2,
      sorted: sortBy.status,
      sortable: true,
    },
    {
      label: "Created On",
      width: 2,
      sorted: sortBy.createdOn,
      sortable: true,
    },
    {
      label: "Tags",
      width: 2,
      sorted: sortBy.tags,
      sortable: false,
    },
  ];

  interface ManageSurvey {
    id: number;
    checked: boolean;
    attributes: {
      name: JSX.Element;
      status: JSX.Element;
      createdOn: string;
      tags: JSX.Element;
    };
  }

  const tableItems: ManageSurvey[] = [];

  sortedQuestionnaires.data.forEach((questionnaire, i) => {
    const name = (
      <div className="surveyName" onClick={() => null}>
        <FontAwesomeIcon className="tabIcon" icon={faPencil} />
        <span>{questionnaire.attributes.name}</span>
      </div>
    );

    const status = (
      <span
        className={`surveyStatus ${
          questionnaire.attributes.status == "Published"
            ? "completed"
            : "pending"
        }`}
      >
        {questionnaire.attributes.status}
      </span>
    );

    tableItems.push({
      id: questionnaire.id,
      checked: questionnaire.checked,
      attributes: {
        name,
        status,
        createdOn: questionnaire.attributes.createdOn,
        tags: getSurveyTags(questionnaire.attributes.tags),
      },
    });
  });

  const handleSort = (column: string, keepOrder?: boolean) => {
    const split = column.split(" ");
    let name = split[split.length - 1];
    if (split[0] === "Created") {
      name = split[0].toLowerCase() + name;
    } else {
      name = name.toLowerCase();
    }
    const sorted = sortItemsByAttribute(
      name == "createdOn" ? null : sortedQuestionnaires,
      name,
      sortBy,
      keepOrder
    );

    if (name !== "createdOn") {
      setSortedQuestionnaires(sorted.data);
    } else {
      if (sorted.columns.createdOn !== "") {
        setSortedQuestionnaires({
          ...sortedQuestionnaires,
          data: sortItemsByDate(sortedQuestionnaires.data, name, sortBy),
        });
      }
    }

    setSortBy(sorted.columns);
  };

  return (
    <>
      <FEPaginatedTable
        tableType="surveys"
        tableItemsData={{
          ...surveysData,
          data: tableItems,
        }}
        columnHeaders={columnHeaders}
        checkboxes={true}
        filtered={filtering}
        checkedItems={selected}
        setCheckedItems={setSelected}
        onSort={handleSort}
        pageSizeEnabled
      />
    </>
  );
};

export default ManageSurveys;
